import { CaretDownIcon } from '@phosphor-icons/react';

import { TextDiff } from '@/components/ai-elements/text-diff';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { StatusBadge } from '@/components/ui/status-badge';
import type {
  AddressPassRecord,
  AddressRejectionRecord,
  AddressedRecord,
} from '@/lib/content-doctrine/article-address';
import { addressOutcomeLine } from '@/lib/content-doctrine/article-address-carry';
import type { AddressNoteCounts } from '@/lib/content-doctrine/article-address-scope';
import { formatWhen } from '@/lib/format-when';
import { COLLAPSIBLE_MOTION, ITEM_META, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/**
 * What an "Address review notes" pass did, and what it left, read from the version it made (`addressed`) or from the
 * version it ran against and changed nothing on (`addressPass`).
 *
 * Every edit shows its own diff, built on the existing word-diff (`TextDiff`, `split` mode: the text before and the
 * text after as two stacked blocks, changed words marked in each), under the note it answered and marked Addressed. A note the guards refused is marked "Couldn't address" with the reason in words. The counts
 * compare this version with the one it started from, so an edit that made things worse reads as worse.
 */

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export interface AddressedNow {
  /** Open findings and unresolved wrong-law claims on THIS version, or null while its checks have not run. */
  openNotes: number | null;
  wrongLaw: number | null;
  /**
   * Of the notes the pass refused, how many are still open (`stillOpenAfterAddress`), or null while the version's
   * checks have not finished. A refused note whose passage is unchanged stays open whatever a fresh critique says.
   */
  stillOpen?: number | null;
  /**
   * "Closed X · Still open Y · New Z" for the notes the pass started from (`addressNoteCounts`), or null while the
   * version's critique has not finished. New notes can only come from the passages the pass edited.
   */
  counts?: AddressNoteCounts | null;
}

function movement(before: number, now: number | null): string {
  return now === null ? `${before} → checking` : `${before} → ${now}`;
}

const flat = (text: string) => text.replace(/\s+/g, ' ').trim();
const clip = (text: string, max = 180) =>
  flat(text).length > max ? `${flat(text).slice(0, max - 1)}…` : flat(text);

/** What the panels need of a refused note: the reason, and the proposed text and verdict when they were kept. */
export type AddressRefusalView = Pick<AddressRejectionRecord, 'why' | 'proposal' | 'verifier'>;

/**
 * Why a note was not addressed, and, when the fixer proposed text that was refused, that text as a collapsible diff
 * beside the checker's verdict, so a person can audit the refusal instead of taking it on trust. Used under the note
 * in the critique panel and in the list of refusals under "Addressed review notes".
 */
export function AddressRefusalDetail({ refusal }: { refusal: AddressRefusalView }): JSX.Element {
  const { proposal, verifier } = refusal;
  const changed = verifier?.changedAssertions.map(flat).filter(Boolean) ?? [];
  return (
    <div className="min-w-0 max-w-full space-y-1">
      <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>{refusal.why}</p>
      {proposal ? (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              Show the refused edit
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-2 pt-1')}
          >
            <div className="min-w-0 max-w-full break-words border-l-2 border-border pl-3">
              <TextDiff
                before={proposal.before}
                after={proposal.after}
                mode="split"
                className="text-caption"
              />
            </div>
            {proposal.evidenceQuote ? (
              <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                Offered as evidence: “{clip(proposal.evidenceQuote, 220)}”
              </p>
            ) : null}
            {verifier ? (
              <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                {verifier.preservesMeaning && !changed.length
                  ? 'The meaning check found the same meaning.'
                  : `The meaning check found a different meaning${changed.length ? `: ${clip(changed.join('; '), 260)}` : verifier.reason ? `: ${clip(verifier.reason, 260)}` : ''}.`}
              </p>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}

export function EditorialAddressed({
  addressed,
  pass,
  now,
}: {
  addressed: AddressedRecord | null | undefined;
  pass: AddressPassRecord | null | undefined;
  now: AddressedNow;
}): JSX.Element | null {
  if (!addressed && !pass) return null;
  const rejected = addressed?.rejected ?? pass?.rejected ?? [];
  const worse =
    addressed &&
    ((now.wrongLaw !== null && now.wrongLaw > addressed.before.wrongLaw) ||
      (now.openNotes !== null && now.openNotes > addressed.before.openNotes));
  return (
    <section className="min-w-0 max-w-full space-y-2 overflow-x-hidden">
      <p className={SECTION_LABEL}>Addressed review notes</p>
      {addressed ? (
        <>
          <p className={ITEM_META}>
            {plural(addressed.edits.length, 'passage was', 'passages were')} rewritten{' '}
            {formatWhen(addressed.addressedAt)}. Nothing else in the article changed.
          </p>
          <p className="text-caption font-medium text-foreground">
            {addressOutcomeLine({
              addressed: addressed.edits.length,
              refused: addressed.rejected.length,
              stillOpen: now.stillOpen ?? null,
            })}
          </p>
          <p className={ITEM_META}>
            {now.counts
              ? `Closed ${now.counts.closed} · Still open ${now.counts.stillOpen} · New ${now.counts.new}`
              : 'Closed, still open and new: checking'}
            . Wrong-law claims {movement(addressed.before.wrongLaw, now.wrongLaw)}.
          </p>
          {worse ? (
            <p className="text-caption text-destructive">
              This version has more open than the one before it. If the edits are not an
              improvement, restore the earlier version under Versions.
            </p>
          ) : null}
        </>
      ) : (
        <p className={ITEM_META}>
          {formatWhen(pass!.ranAt)}: no edit could be made within the rules, so the article is
          unchanged. Each note below says why.
        </p>
      )}
      {addressed?.edits.length ? (
        <Collapsible defaultOpen>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              {plural(addressed.edits.length, 'edit', 'edits')}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-4 pt-2')}
          >
            {addressed.edits.map((edit) => (
              <div key={edit.id + edit.key} className="min-w-0 max-w-full space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone="success" fill="outline">
                    Addressed
                  </StatusBadge>
                  <span className={cn(ITEM_META, 'min-w-0 break-words')}>
                    {edit.kind === 'claim' ? 'Wrong-law claim' : 'Review note'}
                  </span>
                </div>
                <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>{clip(edit.note)}</p>
                <div className="min-w-0 max-w-full break-words border-l-2 border-border pl-3">
                  <TextDiff
                    before={edit.before}
                    after={edit.after}
                    mode="split"
                    className="text-caption"
                  />
                </div>
                {edit.evidenceBacked.length ? (
                  <p className={cn(ITEM_META, 'min-w-0 max-w-full break-words')}>
                    Changed {edit.evidenceBacked.slice(0, 3).join(', ')}, backed by the stored
                    evidence: “{clip(edit.evidenceQuote ?? '', 220)}”
                  </p>
                ) : null}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {rejected.length ? (
        <Collapsible defaultOpen={!addressed}>
          <CollapsibleTrigger asChild>
            <Button variant="flat" size="sm" className="group -ml-2">
              <CaretDownIcon className="transition-transform group-data-[state=closed]:-rotate-90" />
              {plural(rejected.length, 'note', 'notes')} couldn&apos;t be addressed
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent
            className={cn(COLLAPSIBLE_MOTION, 'min-w-0 max-w-full space-y-3 pt-2')}
          >
            {rejected.map((entry, index) => (
              <div key={`${entry.key}-${index}`} className="min-w-0 max-w-full space-y-1">
                <StatusBadge tone="warning" fill="outline">
                  Couldn&apos;t address
                </StatusBadge>
                <p className="min-w-0 max-w-full break-words border-l-2 border-border pl-3 text-caption leading-relaxed">
                  {clip(entry.quote)}
                </p>
                <AddressRefusalDetail refusal={entry} />
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </section>
  );
}
